import { Delete } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import Moment from "moment";
import { useEffect, useState } from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import ProtectedPermissionView from "./ProtectedPermissionView";

interface SalesFormProps {
  form: UseFormReturn<any>;
  branches: any[];
  loading: boolean;
  isEdit: boolean;
  onSubmit: (data: any) => void;
  onCancel?: () => void;
}

export default function SalesForm({
  form,
  branches,
  loading,
  isEdit,
  onSubmit,
  onCancel,
}: SalesFormProps) {
  const { control, handleSubmit, getValues, setValue: setFormValue } = form;
  const [dateValue, setDateValue] = useState<Dayjs | null>(dayjs(new Date()));
  const [photoGantiKertas, setPhotoGantiKertas] = useState<File[]>([]);
  const [photoPrintCount, setPhotoPrintCount] = useState<File[]>([]);
  const [photoQrManual, setPhotoQrManual] = useState<File[]>([]);
  const [photoFraud, setPhotoFraud] = useState<File[]>([]);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    setFormValue(
      "date",
      Moment(dateValue?.format("YYYY-MM-DD")).format("YYYY-MM-DD")
    );
  }, [dateValue, setFormValue]);

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    setImages: React.Dispatch<React.SetStateAction<File[]>>,
    field: string
  ) => {
    if (event.target.files) {
      const fileList = Array.from(event.target.files);
      setImages((prevImages) => [...prevImages, ...fileList]);
      const currentValues = getValues(field) as File[];
      setFormValue(field, [...currentValues, ...fileList] as any);
    }
  };

  const handleDelete = (
    index: number,
    setImages: React.Dispatch<React.SetStateAction<File[]>>,
    images: File[],
    field: string
  ) => {
    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages);
    setFormValue(field, updatedImages);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container>
        <Grid item lg={5} xs={11} mr={1}>
          <FormControl fullWidth margin="normal">
            <FormLabel required>Branch</FormLabel>
            <Controller
              name="branchid"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={branches}
                  getOptionLabel={(option: any) => option.name}
                  onChange={(event, newValue) => {
                    setFormValue("branchid", newValue ? newValue.id : 0);
                  }}
                  filterOptions={(options, { inputValue }) =>
                    options.filter((option) =>
                      option.name
                        .toLowerCase()
                        .includes(inputValue.toLowerCase())
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Branch"
                      variant="outlined"
                      value={filter}
                      onChange={(e: any) => {
                        setFilter(e.target.value);
                        if (params.inputProps.onChange) {
                          params.inputProps.onChange(e);
                        }
                      }}
                    />
                  )}
                  disabled={!!isEdit}
                  noOptionsText="No branches available"
                  value={branches.find((branch: any) => branch.id === getValues().branchid) || null}
                />
              )}
            />
          </FormControl>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <FormLabel required>PIC Pagi</FormLabel>
            <Controller
              name="pic_pagi"
              control={control}
              render={({ field: { ref, ...field }, fieldState }) => (
                <TextField
                  {...field}
                  inputRef={ref}
                  error={Boolean(fieldState.error?.message)}
                  helperText={fieldState.error?.message}
                  size="small"
                />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item lg={5} xs={11} mr={1}>
          <FormControl fullWidth margin="normal">
            <FormLabel required>Date</FormLabel>
            <Controller
              name="date"
              control={control}
              render={({ field: { ref, ...field }, fieldState }) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    inputFormat="DD/MM/YYYY"
                    value={dateValue}
                    disabled={!!isEdit}
                    onChange={(newValue) => {
                      setDateValue(newValue);
                    }}
                    renderInput={(props) => <TextField {...props} />}
                  />
                </LocalizationProvider>
              )}
            />
          </FormControl>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <FormLabel required>PIC Malam</FormLabel>
            <Controller
              name="pic_malam"
              control={control}
              render={({ field: { ref, ...field }, fieldState }) => (
                <TextField
                  {...field}
                  inputRef={ref}
                  error={Boolean(fieldState.error?.message)}
                  helperText={fieldState.error?.message}
                  size="small"
                />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item lg={5} xs={11}>
          <Grid container>
            <Grid item sm={5} mr={1}>
              <FormControl fullWidth margin="normal">
                <FormLabel>Beginning</FormLabel>
                <Controller
                  name="beginning"
                  control={control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item sm={5} mr={1}>
              <FormControl fullWidth margin="normal">
                <FormLabel>Tambah Kertas Baru</FormLabel>
                <Controller
                  name="kertas_baru"
                  control={control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item sm={5} mr={1}>
              <FormControl fullWidth margin="normal">
                <FormLabel>Kertas Hilang</FormLabel>
                <Controller
                  name="kertas_hilang_kebuang"
                  control={control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item sm={5} mr={1}>
              <FormControl fullWidth margin="normal">
                <FormLabel>Ending</FormLabel>
                <Controller
                  name="ending"
                  control={control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
        <Grid item lg={5} xs={11}>
          <Grid container>
            <Grid item sm={5} mr={1}>
              <FormControl fullWidth margin="normal">
                <FormLabel>Struk Cash</FormLabel>
                <Controller
                  name="struk_cash"
                  control={control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item sm={5} mr={1}>
              <FormControl fullWidth margin="normal">
                <FormLabel>Struk QR</FormLabel>
                <Controller
                  name="struk_qr"
                  control={control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item sm={5} mr={1}>
              <FormControl fullWidth margin="normal">
                <FormLabel>Print Count</FormLabel>
                <Controller
                  name="print_count"
                  control={control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item sm={5} mr={1}>
              <FormControl fullWidth margin="normal">
                <FormLabel>Total Fraud</FormLabel>
                <Controller
                  name="total_kesalahan_fraud"
                  control={control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
        <Grid item lg={5} xs={11}>
          <Grid container>
            <Grid item sm={5} mr={1}>
              <FormControl fullWidth margin="normal">
                <FormLabel>Crew Teman Photo</FormLabel>
                <Controller
                  name="crew_teman"
                  control={control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item sm={5} mr={1}>
              <FormControl fullWidth margin="normal">
                <FormLabel>Free Voucher</FormLabel>
                <Controller
                  name="free_voucher"
                  control={control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item sm={5} mr={1}>
              <FormControl fullWidth margin="normal">
                <FormLabel>Double Print</FormLabel>
                <Controller
                  name="double_print"
                  control={control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item sm={5} mr={1}>
              <FormControl fullWidth margin="normal">
                <FormLabel>Print Error / Test</FormLabel>
                <Controller
                  name="print_error_test"
                  control={control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
        <Grid item lg={5} xs={11}>
          <Grid container>
            <Grid item sm={5} mr={1}>
              <FormControl fullWidth margin="normal">
                <FormLabel>Kecurangan Customer</FormLabel>
                <Controller
                  name="fraud"
                  control={control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item sm={5} mr={1}>
              <FormControl fullWidth margin="normal">
                <FormLabel>Lainnya</FormLabel>
                <Controller
                  name="lainnya"
                  control={control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid item sm={12} mr={1}>
            <ProtectedPermissionView acceptPermissions={[1, 8]}>
              <FormControl margin="normal">
                <FormLabel>Validasi</FormLabel>
                <Controller
                  name="validasi_check"
                  control={control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
            </ProtectedPermissionView>
          </Grid>
        </Grid>
        <Grid item sm={10} mr={1}>
          <FormControl fullWidth margin="normal">
            <FormLabel>Keterangan Lainnya</FormLabel>
            <Controller
              name="keterangan"
              control={control}
              render={({ field: { ref, ...field }, fieldState }) => (
                <TextField
                  {...field}
                  inputRef={ref}
                  error={Boolean(fieldState.error?.message)}
                  helperText={fieldState.error?.message}
                  size="small"
                />
              )}
            />
          </FormControl>
        </Grid>
      </Grid>
      {!isEdit && (
        <Grid container>
          <Grid item lg={5} sm={5} mr={1}>
            <FormControl fullWidth margin="normal">
              <Controller
                name="kertas"
                control={control}
                render={({ field }) => (
                  <>
                    <input
                      type="file"
                      name="kertas"
                      multiple
                      onChange={(event) =>
                        handleFileChange(
                          event,
                          setPhotoGantiKertas,
                          "kertas"
                        )
                      }
                      hidden
                    />
                    <Button
                      variant="contained"
                      component="span"
                      onClick={() => {
                        let element: HTMLElement = document.querySelector(
                          'input[name="kertas"]'
                        )!;
                        element.click();
                      }}
                    >
                      Upload Photo Ganti Kertas
                    </Button>
                  </>
                )}
              />
              <List>
                {photoGantiKertas.map((photo, index) => (
                  <ListItem
                    key={index}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() =>
                          handleDelete(
                            index,
                            setPhotoGantiKertas,
                            photoGantiKertas,
                            "kertas"
                          )
                        }
                      >
                        <Delete />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={photo.name} />
                  </ListItem>
                ))}
              </List>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <Controller
                name="print"
                control={control}
                render={({ field }) => (
                  <>
                    <input
                      type="file"
                      name="print"
                      multiple
                      onChange={(event) =>
                        handleFileChange(event, setPhotoPrintCount, "print")
                      }
                      hidden
                    />
                    <Button
                      variant="contained"
                      component="span"
                      onClick={() => {
                        let element: HTMLElement = document.querySelector(
                          'input[name="print"]'
                        )!;
                        element.click();
                      }}
                    >
                      Upload Photo Print Count
                    </Button>
                  </>
                )}
              />
              <List>
                {photoPrintCount.map((photo, index) => (
                  <ListItem
                    key={index}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() =>
                          handleDelete(
                            index,
                            setPhotoPrintCount,
                            photoPrintCount,
                            "print"
                          )
                        }
                      >
                        <Delete />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={photo.name} />
                  </ListItem>
                ))}
              </List>
            </FormControl>
          </Grid>
          <Grid item lg={5} sm={5} mr={1}>
            <FormControl fullWidth margin="normal">
              <Controller
                name="qr"
                control={control}
                render={({ field }) => (
                  <>
                    <input
                      name="qr"
                      type="file"
                      multiple
                      onChange={(event) =>
                        handleFileChange(event, setPhotoQrManual, "qr")
                      }
                      hidden
                    />
                    <Button
                      variant="contained"
                      component="span"
                      onClick={() => {
                        let element: HTMLElement =
                          document.querySelector('input[name="qr"]')!;
                        element.click();
                      }}
                    >
                      Upload Photo QR Manual
                    </Button>
                  </>
                )}
              />
              <List>
                {photoQrManual.map((photo, index) => (
                  <ListItem
                    key={index}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() =>
                          handleDelete(
                            index,
                            setPhotoQrManual,
                            photoQrManual,
                            "qr"
                          )
                        }
                      >
                        <Delete />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={photo.name} />
                  </ListItem>
                ))}
              </List>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <Controller
                name="file_fraud"
                control={control}
                render={({ field }) => (
                  <>
                    <input
                      name="file_fraud"
                      type="file"
                      multiple
                      onChange={(event) =>
                        handleFileChange(event, setPhotoFraud, "file_fraud")
                      }
                      hidden
                    />
                    <Button
                      variant="contained"
                      component="span"
                      onClick={() => {
                        let element: HTMLElement = document.querySelector(
                          'input[name="file_fraud"]'
                        )!;
                        element.click();
                      }}
                    >
                      Upload Photo Fraud
                    </Button>
                  </>
                )}
              />
              <List>
                {photoFraud.map((photo, index) => (
                  <ListItem
                    key={index}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() =>
                          handleDelete(
                            index,
                            setPhotoFraud,
                            photoFraud,
                            "fraud"
                          )
                        }
                      >
                        <Delete />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={photo.name} />
                  </ListItem>
                ))}
              </List>
            </FormControl>
          </Grid>
        </Grid>
      )}
      <Box sx={{ mt: 3 }}>
        <Stack direction="row" spacing={1}>
          <LoadingButton
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            loading={loading}
          >
            SAVE
          </LoadingButton>

          <Button
            variant="outlined"
            onClick={onCancel}
          >
            CANCEL
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
